import { keepPreviousData } from '@tanstack/react-query'

import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import { isUserAllowed, useMe } from '@shared/providers/src/MeProvider'
import { AssessmentStatus, UserRole } from '@shared/utils'

import CopyIconButton from '@components/CopyIconButton'
import { spacing } from '@components/Details/Details.utils'
import EntityViewers from '@components/EntityViewers'
import { usePatientAccessible } from '@components/PatientAccessGuard'

import { invalidateEncounterNotes, useAssessmentDetails } from './AssessmentDetailsModal.hooks'
import AnswersSection from './components/AnswersSection'
import DetailsSection from './components/DetailsSection'
import NotesSection from './components/NotesSection'
import PatientSection from './components/PatientSection'
import ReviewActions from './components/ReviewActions'

export default function AssessmentDetailsModal({
  assessmentId,
  hidePatientData = false,
  align = 'flex-end',
  canMarkExceptional = false,
  open,
  onClose,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      sx={{ '& .MuiDialog-container': { justifyContent: align } }}
    >
      <Content assessmentId={assessmentId} hidePatientData={hidePatientData} canMarkExceptional={canMarkExceptional} onClose={onClose} />
    </Dialog>
  )
}

function Content({ assessmentId, hidePatientData = false, canMarkExceptional = false, onClose }) {
  const me = useMe()
  const { data: assessment } = useAssessmentDetails(assessmentId, { enabled: Boolean(assessmentId), placeholderData: keepPreviousData })

  const patientId = assessment?.userId
  const encounterId = assessment?.encounterId
  const patientAccessible = usePatientAccessible(patientId)
  const isAllowed = isUserAllowed(me, [
    UserRole.Admin,
    UserRole.Provider,
    UserRole.ClinicalAdmin,
    UserRole.ClinicalLHC,
    UserRole.ClinicalQI,
  ])
  const canAddNotes = isAllowed && patientAccessible
  const showData = Boolean(assessment)

  return (
    <>
      <DialogTitle onClose={onClose}>
        {showData ? (
          <Stack direction="row" sx={{ width: '100%', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Stack spacing={-0.5}>
              <Stack direction="row" spacing={1}>
                <Typography sx={{ fontWeight: 'normal', color: 'text.secondary' }}>ID: {assessment.id}</Typography>
                <CopyIconButton fn={(copy) => copy(assessment.id)} />
              </Stack>
              <Typography component="h2" variant="h4">
                {assessment.name}
              </Typography>
            </Stack>
            <EntityViewers key={assessment.id} entityType="assessment" entityId={assessment.id} />
          </Stack>
        ) : (
          <Stack spacing={-0.5}>
            <Typography>
              <Skeleton width={100} />
            </Typography>
            <Typography component="h2" variant="h4">
              <Skeleton width={300} />
            </Typography>
          </Stack>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={spacing}>
          {showData ? (
            <>
              {!hidePatientData && <PatientSection patientId={patientId} mini />}
              <DetailsSection assessment={assessment} />
              <AnswersSection assessment={assessment} patientId={patientId} canMarkExceptional={canMarkExceptional} />
              <ReviewActions patientId={patientId} encounterId={encounterId} assessment={assessment} onSuccess={onClose} />
              <NotesSection assessmentId={assessmentId} readOnly={!canAddNotes} onCreate={() => invalidateEncounterNotes(assessment)} />
            </>
          ) : (
            <>
              {!hidePatientData && <PatientSection.Loading mini />}
              <DetailsSection.Loading />
              <AnswersSection.Loading />
            </>
          )}
        </Stack>
      </DialogContent>
    </>
  )
}

export function getAssessmentStatusColor(status) {
  switch (status) {
    case AssessmentStatus.Finished:
      return 'warning'
    case AssessmentStatus.Reviewed:
      return 'success'
    case AssessmentStatus.Pending:
      return 'info'
    case AssessmentStatus.Expired:
      return 'error'
    default:
      return 'default'
  }
}
